// ─── Easing ───────────────────────────────────────────────
export type EasingMode =
  | 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'step' | 'cubic-bezier';

// ─── Keyframes & Effects ──────────────────────────────────
export interface Keyframe<T> {
  time: number;
  value: T;
  easing: EasingMode;
  bezierPoints?: [number, number, number, number];
}

export interface Effect<T = any> {
  id: string;
  property: string;
  keyframes: Keyframe<T>[];
  enabled: boolean;
}

// ─── Layers ───────────────────────────────────────────────
export interface BaseLayerData {
  id: string;
  type: string;
  name: string;
  order: number;
  parentId: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  opacity: number;
  startTime: number;
  endTime: number;
  shadowColor: string;
  shadowBlur: number;
  shadowOffsetX: number;
  shadowOffsetY: number;
  shadowOpacity: number;
  visible: boolean;
  locked: boolean;
  effects: Record<string, Effect>;
  [key: string]: any;
}

// ─── Layer Type Definition ────────────────────────────────
export interface PropertyDefinition {
  type: string;
  default: any;
  values?: string[];
}

export interface LayerTypeDefinition {
  type: string;
  canHaveChildren: boolean;
  renderable: boolean;
  properties: Record<string, PropertyDefinition>;
  animatable: string[];
  serialize?: (layer: BaseLayerData) => Record<string, any>;
  deserialize?: (data: Record<string, any>) => Partial<BaseLayerData>;
}

// ─── Commands ─────────────────────────────────────────────
export interface Command<T = any> {
  type: string;
  __payloadType?: T;
}

export type CommandHandler<T = any> = (
  state: EditorState,
  payload: T,
) => { state: EditorState; inverse: InverseOperation | null };

export interface InverseOperation {
  command: Command<any>;
  payload: any;
}

// ─── Middleware ────────────────────────────────────────────
export interface MiddlewareContext {
  getState: () => EditorState;
  getOption: <T>(key: string, defaultValue: T) => T;
}

export type MiddlewareFn<T = any> = (payload: T, context: MiddlewareContext) => T | null;

export interface Middleware {
  command: Command<any>;
  fn: MiddlewareFn;
}

// ─── Interpolators ────────────────────────────────────────
export interface Interpolator<T = any> {
  lerp: (from: T, to: T, t: number) => T;
  properties: string[];
}

// ─── Editor State ─────────────────────────────────────────
export interface EditorState {
  layers: Record<string, BaseLayerData>;
  rootLayerId: string;
  selectedIds: Set<string>;
  primarySelectedId: string | null;
  playheadTime: number;
  isPlaying: boolean;
}

/**
 * Helper to read composition properties from the root layer.
 * The root layer is the source of truth for canvas size, duration, and frameRate.
 */
export function getCompositionProps(state: EditorState): {
  width: number;
  height: number;
  duration: number;
  frameRate: number;
  name: string;
} {
  const root = state.layers[state.rootLayerId];
  return {
    width: root?.width ?? 1920,
    height: root?.height ?? 1080,
    duration: root?.duration ?? 0,
    frameRate: root?.frameRate ?? 24,
    name: root?.name ?? 'Untitled',
  };
}

// ─── Editor Config ────────────────────────────────────────
export interface EditorConfig {
  layerTypes?: LayerTypeDefinition[];
  middleware?: Middleware[];
  canvas?: { width: number; height: number };
  frameRate?: number;
  devtools?: boolean;
  onError?: (error: Error) => void;
}

// ─── Listener Types ───────────────────────────────────────
export type CommandListener<T = any> = (payload: T) => void;
export type WildcardListener = (command: Command<any>, payload: any) => void;
export type StateListener = (state: EditorState) => void;
export type Unsubscribe = () => void;

// ─── Special Tokens ───────────────────────────────────────
export const WILDCARD: Command<any> = { type: '__WILDCARD__' };
